import { Button } from 'antd';
import { config } from 'config/config';
import getConfig from 'next/config';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, Dispatch, bindActionCreators } from 'redux';
import {
  setShowDiscoveryModal,
  setShowSpecialDiscoveryCoupon,
  setUserCampaigns,
} from 'src/actions/appActions';
import { IRootReducers } from 'src/reducers';
import { ICampaign, IDiscoveryCoupon } from 'src/shared/models';
import { useWindowSize } from 'utils';
import { PlatformEnum } from 'src/shared/enums';
import {
  getDiffInMiliseconds,
  getTimeFormat,
} from './discoveryGetTimeFunctions';
import Router from 'next/router';
import { DiscoveryCampaignsConfig } from 'config/discovery-campaigns';

const { publicRuntimeConfig } = getConfig();

export interface specialDiscoveryCouponData {
  fromLayout?: boolean;
  campaign: ICampaign;
  discoveryCoupons: IDiscoveryCoupon[];
}

const {
  CON_ARGENTINA,
  CON_CHILE,
  CON_COLOMBIA,
  CON_MEXICO,
  CON_PERU,
  CON_FRANCE,
  CON_SPAIN,
} = PlatformEnum;

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  specialDiscoveryCouponData;

const ConsumableSpecialDiscoveryCoupon: FC<Props> = ({
  fromLayout,
  campaign,
  setShowDiscoveryModal,
  setShowSpecialDiscoveryCoupon,
  setUserCampaigns,
}): JSX.Element => {
  const srcSpecialCouponDesktop = `${config.APP.MEDIA_CON}DiscoverySpecialCoupon_Desktop_v1.png`;
  const srcSpecialCouponMobile = `${config.APP.MEDIA_CON}DiscoverySpecialCoupon_Mobile_v1.png`;

  const { isSM } = useWindowSize();

  const { formatMessage } = useIntl();
  const [disabledButton, setDisabledButton] = useState(false);
  const [clickDiscoveryButton, setClickDiscoveryButton] = useState(true);
  const [time, setTime] = useState(getDiffInMiliseconds(campaign));
  const [timeFormat, setTimeFormat] = useState(getTimeFormat(time));

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!campaign) {
        setDisabledButton(true);
        setShowSpecialDiscoveryCoupon(false);
        setUserCampaigns();
      }
      setTime(time - 1000);
    }, 1000);
    return () => clearInterval(intervalId);
  });

  useEffect(() => {
    if (time < 1000) {
      setDisabledButton(true);
      setShowSpecialDiscoveryCoupon(false);
      setUserCampaigns();
    } else {
      setTimeFormat(getTimeFormat(time));
    }
  }, [time]);

  const isCampaignDiscoverySpecial =
    campaign && campaign?.type && !!DiscoveryCampaignsConfig?.[campaign.type];
  let campaignSpecial;
  if (isCampaignDiscoverySpecial) {
    campaignSpecial = DiscoveryCampaignsConfig?.[campaign?.type];
  }

  const convertToTwoDigit = (digit: number) => {
    return digit.toLocaleString('en-US', {
      minimumIntegerDigits: 2,
    });
  };

  const specialCouponImg = (): string => {
    if (isSM) {
      if (isCampaignDiscoverySpecial) {
        return campaignSpecial?.images?.banner?.myCoupons?.mobile;
      }
      return srcSpecialCouponMobile;
    } else {
      if (isCampaignDiscoverySpecial) {
        return campaignSpecial?.images?.banner?.myCoupons?.desktop;
      }
      return srcSpecialCouponDesktop;
    }
  };

  const generalLayoutStyle = (fromLayout: boolean): string => {
    if (fromLayout) return 'containList';
    else return 'containMyCoupons';
  };

  const myCouponsTextStyle = (fromLayout: boolean): string => {
    const countryPlatform = config.APP.PLATFORM;
    let styleClass;
    switch (countryPlatform) {
      case CON_ARGENTINA:
      case CON_CHILE:
      case CON_COLOMBIA:
      case CON_FRANCE:
      case CON_MEXICO:
      case CON_PERU:
      case CON_SPAIN:
        styleClass = fromLayout
          ? 'specialCouponTextContainerFromLayout specialCouponTextSpanishMobile'
          : 'specialCouponTextContainer specialCouponTextContainerWidthSpanish';
        break;
      default:
        styleClass = fromLayout
          ? 'specialCouponTextContainerFromLayout specialCouponTextNotSpanishMobile'
          : 'specialCouponTextContainer specialCouponTextContainerWidthNotSpanish';
    }
    return styleClass;
  };

  const myCouponsTextStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial
      ? { color: campaignSpecial?.styles?.banner?.myCoupons?.title }
      : undefined;
  };

  const specialCouponClockStyle = (fromLayout: boolean): string => {
    if (fromLayout) return 'specialCouponClockFromLayout';
    else return 'specialCouponClock';
  };

  const couponClockDigitStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial
      ? { color: campaignSpecial?.styles?.banner?.myCoupons?.clock }
      : undefined;
  };

  const couponClockDateStylesCampaign = (): { color: string } => {
    return isCampaignDiscoverySpecial
      ? { color: campaignSpecial?.styles?.banner?.myCoupons?.date }
      : undefined;
  };

  const specialCouponButtonContainerStyle = (fromLayout: boolean): string => {
    if (fromLayout) return 'specialCouponButtonContainerFromLayout';
    else return 'specialCouponButtonContainer';
  };

  const specialCouponButtonTextStyle = (): string => {
    const btnPrimaryColor = !isCampaignDiscoverySpecial
      ? 'btn-primary--black'
      : campaignSpecial?.styles?.banner?.myCoupons?.btnPrimaryColor;
    let styles = `btn-primary ${btnPrimaryColor} qr-landing__login__form__button__logged specialDiscoveryButton ${checkSpecialButtonLeftMargin()}`;
    return styles;
  };

  const discoverCoupon = async () => {
    if (clickDiscoveryButton) {
      const url = new URL(window.location.href);
      url.searchParams.append('campaign', campaign.id);
      history.pushState(null, null, url.toString());
      setShowDiscoveryModal(true);
    } else {
      Router.push({
        pathname: publicRuntimeConfig.PLATFORM_PREFIX,
      });
    }
    setClickDiscoveryButton(!clickDiscoveryButton);
  };

  const checkSpecialButtonLeftMargin = (): string => {
    const countryPlatform = config.APP.PLATFORM;
    switch (countryPlatform) {
      case CON_ARGENTINA:
      case CON_CHILE:
      case CON_COLOMBIA:
      case CON_FRANCE:
      case CON_MEXICO:
      case CON_PERU:
      case CON_SPAIN:
        return 'specialDiscoveryButtonLeftMargin';
      default:
        return 'specialDiscoveryButtonNotLeftMargin';
    }
  };

  return (
    <div className={generalLayoutStyle(fromLayout)}>
      <img src={specialCouponImg()} />
      <div className="specialCouponContainer">
        <p
          className={myCouponsTextStyle(fromLayout)}
          style={myCouponsTextStylesCampaign()}>
          {formatMessage({
            id: 'consumable.coupon.special.text',
          })}
        </p>

        <div className={specialCouponClockStyle(fromLayout)}>
          <div className="clockDigitContainer">
            <span
              className="clockDigit"
              style={couponClockDigitStylesCampaign()}>
              {convertToTwoDigit(timeFormat.days)}
            </span>
            <p
              className="clockDigitText"
              style={couponClockDateStylesCampaign()}>
              {formatMessage({
                id: 'consumable.coupon.special.clock.days',
              })}
            </p>
          </div>
          <div className="clockDigitContainer">
            <span
              className="clockDigit"
              style={couponClockDigitStylesCampaign()}>
              {convertToTwoDigit(timeFormat.hours)}
            </span>
            <p
              className="clockDigitText"
              style={couponClockDateStylesCampaign()}>
              {formatMessage({
                id: 'consumable.coupon.special.clock.hours',
              })}
            </p>
          </div>
          <div className="clockDigitContainer">
            <span
              className="clockDigit"
              style={couponClockDigitStylesCampaign()}>
              {convertToTwoDigit(timeFormat.minutes)}
            </span>
            <p
              className="clockDigitText"
              style={couponClockDateStylesCampaign()}>
              {formatMessage({
                id: 'consumable.coupon.special.clock.minutes',
              })}
            </p>
          </div>
          <div className="clockDigitContainer">
            <span
              className="clockDigit"
              style={couponClockDigitStylesCampaign()}>
              {convertToTwoDigit(timeFormat.seconds)}
            </span>
            <p
              className="clockDigitText"
              style={couponClockDateStylesCampaign()}>
              {formatMessage({
                id: 'consumable.coupon.special.clock.seconds',
              })}
            </p>
          </div>
        </div>
        <div className={specialCouponButtonContainerStyle(fromLayout)}>
          <Button
            className={specialCouponButtonTextStyle()}
            onClick={discoverCoupon}
            disabled={disabledButton}>
            {formatMessage({
              id: 'consumable.coupon.discovery.button',
            })}
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    userCampaigns: state.app.userCampaigns,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    { setShowDiscoveryModal, setShowSpecialDiscoveryCoupon, setUserCampaigns },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ConsumableSpecialDiscoveryCoupon);
